import type { JSX } from 'react';
import { useState } from 'react';
import type { QuestionOption, WireConversationSummary } from '@logistics/contracts';
import { ConfirmDelete } from './confirm-delete';
import { ConversationList } from './conversation-list';
import { useConversations, useDeleteConversation } from './use-chat-data';
import type { ScopeOption } from './use-chat-data';

/** The conversation list with its data: paging, and deleting one's own after a confirm. */
export function ChatSidebar({
  meId,
  activeId,
  onOpen,
  onPick,
  onDeleted,
  scopes,
  scope,
  onToggleScope,
  className,
}: {
  meId: string;
  activeId: string | null;
  onOpen: (id: string | null) => void;
  /** Any pick, even of the open one or a new conversation — the phone's overlay closes. */
  onPick?: () => void;
  onDeleted: (id: string) => void;
  scopes: readonly ScopeOption[];
  scope: readonly QuestionOption[];
  onToggleScope: (option: QuestionOption) => void;
  className?: string | undefined;
}): JSX.Element {
  const list = useConversations();
  const remove = useDeleteConversation();
  const [deleting, setDeleting] = useState<WireConversationSummary | null>(null);
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  let status: string | undefined;
  if (list.isPending) status = 'Loading…';
  else if (list.isError) status = 'Could not load conversations.';
  else if (items.length === 0) status = 'No conversations yet.';

  const confirm = (target: WireConversationSummary): void => {
    setDeleting(null);
    remove.mutate(target, { onSuccess: (gone) => gone && onDeleted(target.id) });
  };

  return (
    <>
      <ConversationList
        meId={meId}
        items={items}
        activeId={activeId}
        onNew={() => {
          onPick?.();
          onOpen(null);
        }}
        onOpen={(id) => {
          onPick?.();
          if (id !== activeId) onOpen(id);
        }}
        onDelete={setDeleting}
        more={{
          has: list.hasNextPage,
          loading: list.isFetchingNextPage,
          load: () => void list.fetchNextPage(),
          status,
        }}
        scopes={scopes}
        scope={scope}
        onToggleScope={onToggleScope}
        className={className}
      />
      {deleting !== null && (
        <ConfirmDelete
          conversation={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={() => confirm(deleting)}
        />
      )}
    </>
  );
}
